import { useBoardFromContext } from "../../utils/hooks/commonly-stored/uploaded-board/board";
import { Board } from "../../reducers/uploadedBoard";
import BoardFiles from "../internal/BoardFiles";
import { Container, SelectParent } from "./ManufacturersPanelStyles";
import PickAndPlace from "./PickAndPlace";
import { FONT_SIZE } from "../../constants/Styles";
import { SelectContainer, SelectOption } from "../common/FancySelect";
import BoardInventory from "../internal/BoardInventory";
import { useRouter } from "next/router";
import LoadingIcon from "../common/LoadingIcon";
import React, { useCallback, useEffect, useState } from "react";
import NotificationCount from "../common/NotificationCount";
import Notifications from "./Notifications";
import PlacementChecks from "./PickAndPlace/PlacementChecks";
import Button from "../common/Button";
import Modal from "../common/Modal";
import ModalContentWithCloseButton from "../common/ModalContentWithCloseButton";
import Revisions from "./Revisions";
import AssemblyDrawings from "./AssemblyDrawings";

export enum PanelTab {
  PLACEMENT_CHECKS = "Placement checks",
  PICK_AND_PLACE = "Pick and place",
  ASSEMBLY_DRAWINGS = "Assembly drawings",
  INVENTORY = "Inventory",
  FILES = "Files",
  NOTIFICATIONS = "Notifications",
  PREVIOUS_REVISIONS = "Previous revisions",
}

export default function ManufacturersPanel() {
  const router = useRouter();
  const board = useBoardFromContext().board as Board;
  const manufacturingPanelView = router.query.manufacturingPanelView as
    | PanelTab
    | undefined;
  const [showPlacementChecks, setShowPlacementChecks] = useState(false);

  const setManufacturingPanelView = useCallback(
    (panelTab: PanelTab) => {
      const newPath = {
        pathname: router.pathname,
        query: {
          ...router.query,
          manufacturingPanelView: panelTab,
        },
      };
      // update the URL, without re-triggering data fetching
      void router.replace(newPath, undefined, { scroll: false });
    },
    [router],
  );

  useEffect(() => {
    if (
      manufacturingPanelView === undefined ||
      !Object.values(PanelTab).includes(manufacturingPanelView)
    ) {
      setManufacturingPanelView(PanelTab.INVENTORY);
    }
  }, [manufacturingPanelView, setManufacturingPanelView]);

  const closePlacementChecks = () => {
    setShowPlacementChecks(false);
  };

  return (
    <Container>
      <SelectParent>
        <SelectContainer>
          {Object.values(PanelTab).map((tab) => {
            return (
              <SelectOption
                key={tab}
                isSelected={tab === manufacturingPanelView}
                onClick={() => setManufacturingPanelView(tab)}
                fontSize={FONT_SIZE.size16}
              >
                {tab}
                {tab === PanelTab.NOTIFICATIONS && (
                  <NotificationCount
                    numNotifications={
                      board.manufacturing_details?.notifications.length
                    }
                  />
                )}
              </SelectOption>
            );
          })}
        </SelectContainer>
      </SelectParent>
      {manufacturingPanelView == PanelTab.INVENTORY && <BoardInventory />}
      {manufacturingPanelView == PanelTab.PLACEMENT_CHECKS && (
        <>
          <Button
            label={"Open placement checks tool"}
            onClick={() => setShowPlacementChecks(true)}
          />
          <Modal
            isOpen={showPlacementChecks}
            onRequestClose={closePlacementChecks}
          >
            <ModalContentWithCloseButton
              title={"Part placement checks"}
              closeModal={closePlacementChecks}
              fullScreen
            >
              <PlacementChecks />
            </ModalContentWithCloseButton>
          </Modal>
        </>
      )}
      {manufacturingPanelView == PanelTab.PICK_AND_PLACE && <PickAndPlace />}
      {manufacturingPanelView == PanelTab.ASSEMBLY_DRAWINGS && (
        <AssemblyDrawings />
      )}
      {manufacturingPanelView == PanelTab.FILES && <BoardFiles board={board} />}
      {manufacturingPanelView === PanelTab.NOTIFICATIONS && <Notifications />}
      {manufacturingPanelView === PanelTab.PREVIOUS_REVISIONS && <Revisions />}
      {manufacturingPanelView === undefined && <LoadingIcon />}
    </Container>
  );
}
